import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { ReportsType } from "pages/Books/Reports/Reports";
import { useMemo } from "react";
import { useGetForecastReportQuery } from "store/apis/reports";
import { ForecastAccount } from "types/Models/report";
import { ReportFilterType } from "../ReportFilter";

export type ForecastMonthData = {
  summary_amount: number | null;
  is_forecast: boolean;
  has_overlap_actual?: boolean;
  actual_amount?: number | null;
};

export type AccountWithForecastMonths = Omit<ForecastAccount, "types"> & {
  types: AccountWithForecastMonths[];
  monthData: Record<string, ForecastMonthData>;
};

type ForecastParams = {
  forecastStartDate: string;
  forecastEndDate: string;
  activeSetId: string | null;
};

export const useForecastData = ({
  reportType,
  filter,
  forecast,
}: {
  reportType: ReportsType;
  filter: ReportFilterType;
  forecast: ForecastParams;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const { data, isFetching, isSuccess, isError, error } =
    useGetForecastReportQuery(
      {
        groupId,
        entityId,
        reportType,
        startDate: filter.START_DATE || "",
        endDate: filter.END_DATE || "",
        forecastStartDate: forecast.forecastStartDate,
        forecastEndDate: forecast.forecastEndDate,
        assumptionSetId: forecast.activeSetId,
        accountingMethod: filter.ACCOUNTING_METHOD,
        showNonZeroAccounts: filter.HIDE_ZERO,
        viewBy: filter.VIEW_BY || null,
      },
      {
        skip:
          !groupId ||
          !entityId ||
          !reportType ||
          !filter.START_DATE ||
          !filter.END_DATE ||
          !forecast.forecastStartDate ||
          !forecast.forecastEndDate,
      }
    );

  const reportData = data?.report_data;

  const forecastStartDate = reportData?.forecast_start_date || null;
  const forecastEndDate = reportData?.forecast_end_date || null;

  const formattedAccounts = useMemo(() => {
    if (!reportData?.accounts) return [];
    return reportData.accounts.map((acc) => buildAccountWithMonths(acc));
  }, [reportData]);

  const allKeys = useMemo(() => {
    if (!reportData?.accounts?.length) return [];
    const keys = new Set<string>();
    const walk = (list: ForecastAccount[]) => {
      for (const acc of list) {
        for (const entry of acc.level_summary) {
          keys.add(entry.key);
        }
        if (acc.types?.length) walk(acc.types);
      }
    };
    walk(reportData.accounts);
    return Array.from(keys);
  }, [reportData]);

  // Build a map of key → entry by walking all accounts until we find one
  const keyMetaMap = useMemo(() => {
    const map = new Map<
      string,
      { is_forecast: boolean; has_overlap_actual: boolean }
    >();
    const walk = (list: ForecastAccount[]) => {
      for (const acc of list) {
        for (const entry of acc.level_summary) {
          if (!map.has(entry.key)) {
            map.set(entry.key, {
              is_forecast: entry.is_forecast,
              has_overlap_actual: entry.has_overlap_actual ?? false,
            });
          }
        }
        if (acc.types?.length) walk(acc.types);
      }
    };
    if (reportData?.accounts) walk(reportData.accounts);
    return map;
  }, [reportData]);

  const overlapKeys = new Set(
    allKeys.filter((k) => keyMetaMap.get(k)?.has_overlap_actual)
  );

  const actualsKeys = allKeys.filter((k) => {
    const meta = keyMetaMap.get(k);
    return meta && !meta.is_forecast;
  });

  const forecastKeys = allKeys.filter((k) => {
    const meta = keyMetaMap.get(k);
    return meta && meta.is_forecast;
  });

  return {
    accounts: formattedAccounts,
    allKeys,
    actualsKeys,
    forecastKeys,
    overlapKeys,
    forecastStartDate,
    forecastEndDate,
    isFetching,
    isSuccess,
    isError,
    error,
    rawAccounts: reportData?.accounts || [],
  };
};

function buildAccountWithMonths(
  acc: ForecastAccount
): AccountWithForecastMonths {
  const monthData: Record<string, ForecastMonthData> = {};
  for (const entry of acc.level_summary) {
    monthData[entry.key] = {
      summary_amount: entry.summary_amount,
      is_forecast: entry.is_forecast,
      has_overlap_actual: entry.has_overlap_actual,
      actual_amount: entry.actual_amount,
    };
  }
  return {
    ...acc,
    monthData,
    types: (acc.types || []).map((child) => buildAccountWithMonths(child)),
  };
}
